import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, Switch, Checkbox, ImageUploadField,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { X } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { usePopupQuery, usePopupMutation } from '../hooks/usePopupsQuery';
import { usePopupConfigMutation } from '../popups/hooks/usePopupConfig';
import { usePopupImageUpload } from '../popups/hooks/usePopupImageUpload';
import type { PopupRequest, PopupAdminView, PopupSlotCreateInput, PopupTranslation } from '../types/cms';
import { PopupSlotSection } from '../popups/components/PopupSlotSection';
import { TimeSelect5 } from '../popups/components/TimeSelect5';
import { appendSlotDraft, canAddSlotDraft, type SlotDraftInput } from '../popups/lib/slotDraft';
import { buildStaffEntryConfigPayload } from '../popups/lib/staffEntryConfig';
import { PreviewUrlButton } from '../preview/PreviewUrlButton';
import { instantToLocalInput, localInputToInstant } from '../lib/datetimeLocal';
import { LocaleHtmlTabs } from '../components/LocaleHtmlTabs';
import { type ContentLocale } from '../lib/contentLocales';
import { buildLocaleHtmlTranslations, pickLocaleHtml } from '../lib/localeContent';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

// QA-122 — Popup 소개 본문 per-locale 필드 키(PopupTranslation.descriptionHtml).
const POPUP_DESC_HTML_FIELD = 'descriptionHtml';

// BUG-022 통합 (옵션 A): 생성+수정 단일 폼, "개념당 1필드".
// 제출 시 각 개념을 base+config 양쪽 컬럼에 매핑한다(화면 중복 0).
// storefront 공개 serving 은 base 컬럼만 읽으므로 base 값을 반드시 채운다.
//   지역      → location + locationRegion
//   상세주소  → address + locationAddress
//   운영기간  → openDate/closeDate(base) + operatingUntil=closeDate(config)
//   운영시간  → operatingHoursStart/End(config) + hoursLabel="HH:mm - HH:mm"(base, storefront 표시)
//   소개      → descriptionHtml(config) + description=strip-html(base/storefront plain)
// config 전용: operatingDays / lat·lng / snsUrls / basePrice. base 전용: name/thumbnail/accentHex/active/sort.

const DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
type DayKey = (typeof DAY_KEYS)[number];
const DAY_LABEL: Record<DayKey, string> = {
  MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일',
};

interface FormState {
  name: string;
  thumbnailUrl: string;
  accentHex: string;
  isActive: boolean;
  sortOrder: number;
  // 개념당 1필드
  region: string; // → location + locationRegion
  address: string; // → address + locationAddress
  openDate: string; // → openDate
  closeDate: string; // → closeDate + operatingUntil
  hoursStart: string; // "HH:mm" → operatingHoursStart + hoursLabel
  hoursEnd: string; // "HH:mm" → operatingHoursEnd + hoursLabel
  description: string; // HTML → descriptionHtml + description(plain)
  // config 전용
  operatingDays: DayKey[];
  locationLat: string;
  locationLng: string;
  snsUrls: string[];
  free: boolean;
  basePrice: string;
  // ID36: 일별 총 정원(빈 문자열=무제한), 잔여석 공개 노출 토글.
  dailyCapacity: string;
  showRemainingSeats: boolean;
  // QA-183: 예약 수량 제한(빈 문자열=무제한, 임의 디폴트 강제 금지).
  maxPerUser: string;
  minPerReservation: string;
  maxPerReservation: string;
  // L-2: 노출 예약(예약발행). on 이면 [시작, 종료] 윈도우로 공개 노출 게이트(datetime-local 표시값).
  displayReservationEnabled: boolean;
  displayStartLocal: string;
  displayEndLocal: string;
  // QA-189: 예매 오픈 일시(노출 윈도우와 별개, datetime-local 표시값). 빈 값=노출=예매(분리 없음).
  bookingOpenLocal: string;
  // 직원확인 비밀번호: 활성화 토글 + 비번 입력(평문, BE 해시). 단위=팝업 전체 단일(슬롯별 아님).
  //   staffEntryPasswordSet=기존 해시 존재 여부(읽기전용 표시) — 입력칸 빈 값=미변경(기존 비번 유지).
  staffEntryPasswordEnabled: boolean;
  staffEntryPassword: string;
  staffEntryPasswordSet: boolean;
  // L-2: 등록 시 함께 생성할 예약 슬롯(등록 폼에서만 사용).
  slots: PopupSlotCreateInput[];
  // QA-122: locale 별 소개 HTML(en/ja/zh). ko-KR 은 description 정본. 빈 값 = SF ko 폴백.
  descriptionTranslations: Partial<Record<ContentLocale, string>>;
}

const EMPTY_FORM: FormState = {
  name: '', thumbnailUrl: '', accentHex: '#6366f1', isActive: true, sortOrder: 0,
  region: '', address: '', openDate: '', closeDate: '', hoursStart: '', hoursEnd: '', description: '',
  operatingDays: [], locationLat: '', locationLng: '', snsUrls: [], free: false, basePrice: '',
  dailyCapacity: '', showRemainingSeats: false,
  maxPerUser: '', minPerReservation: '', maxPerReservation: '',
  displayReservationEnabled: false, displayStartLocal: '', displayEndLocal: '',
  bookingOpenLocal: '',
  staffEntryPasswordEnabled: false, staffEntryPassword: '', staffEntryPasswordSet: false,
  slots: [],
  descriptionTranslations: {},
};

const parseDays = (raw: string | null | undefined): DayKey[] => {
  if (!raw) return [];
  const set = new Set(raw.split(',').map((s) => s.trim().toUpperCase()));
  return DAY_KEYS.filter((d) => set.has(d));
};

const parseSnsUrls = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((u): u is string => typeof u === 'string') : [];
  } catch {
    return [];
  }
};

const toTimeInput = (raw: string | null | undefined): string => (raw ? raw.slice(0, 5) : '');
const toTimeWire = (v: string): string | null => (v ? `${v}:00` : null);

const numberOrNull = (raw: string): number | null => {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

// HTML → plain text (base description / storefront 용). 태그 제거 + 공백 정리.
const stripHtml = (html: string): string => {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
};

// 운영 시간 표시 라벨(base hoursLabel, storefront 노출).
const hoursLabelFrom = (start: string, end: string): string => {
  if (start && end) return `${start} - ${end}`;
  if (start) return `${start} ~`;
  if (end) return `~ ${end}`;
  return '';
};

// A-2: popupId/popupKey 자동발급(운영자 미노출).
const slugify = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
const shortUid = (): string => crypto.randomUUID().replace(/-/g, '').slice(0, 12);
const generatePopupKey = (name: string): string => {
  const base = slugify(name);
  return base ? `${base}-${shortUid()}`.slice(0, 64) : `popup-${shortUid()}`;
};

const fromView = (d: PopupAdminView): FormState => ({
  name: d.name,
  thumbnailUrl: d.thumbnailUrl,
  accentHex: d.accentHex,
  isActive: d.isActive,
  sortOrder: d.sortOrder,
  // 읽기: config 우선, 없으면 base 폴백(과거 데이터 호환).
  region: d.locationRegion ?? d.location ?? '',
  address: d.locationAddress ?? d.address ?? '',
  openDate: d.openDate,
  closeDate: d.closeDate,
  hoursStart: toTimeInput(d.operatingHoursStart),
  hoursEnd: toTimeInput(d.operatingHoursEnd),
  description: d.descriptionHtml ?? d.description ?? '',
  operatingDays: parseDays(d.operatingDays),
  locationLat: d.locationLat != null ? String(d.locationLat) : '',
  locationLng: d.locationLng != null ? String(d.locationLng) : '',
  snsUrls: parseSnsUrls(d.snsUrls),
  free: d.basePrice === 0,
  basePrice: d.basePrice != null && d.basePrice > 0 ? String(d.basePrice) : '',
  // ID36: 일별 정원 null/미존재 = 무제한(빈 입력). 잔여석 노출은 기본 off.
  dailyCapacity: d.dailyCapacity != null ? String(d.dailyCapacity) : '',
  showRemainingSeats: d.showRemainingSeats ?? false,
  // QA-183: 예약 수량 제한 복원. null/미존재 = 무제한(빈 입력).
  maxPerUser: d.maxPerUser != null ? String(d.maxPerUser) : '',
  minPerReservation: d.minPerReservation != null ? String(d.minPerReservation) : '',
  maxPerReservation: d.maxPerReservation != null ? String(d.maxPerReservation) : '',
  // L-2: 노출 예약 복원. enabled 미존재 = off. start/end Instant → datetime-local 표시값.
  displayReservationEnabled: d.displayReservationEnabled ?? false,
  displayStartLocal: instantToLocalInput(d.displayStartAt),
  displayEndLocal: instantToLocalInput(d.displayEndAt),
  // QA-189: 예매 오픈 일시 복원. 미존재 = 빈 값(노출=예매).
  bookingOpenLocal: instantToLocalInput(d.bookingOpenAt),
  // 직원확인 비밀번호: 토글/설정 여부만 복원(해시/평문은 비노출이라 입력칸은 항상 빈 값으로 시작).
  staffEntryPasswordEnabled: d.staffEntryPasswordEnabled ?? false,
  staffEntryPassword: '',
  staffEntryPasswordSet: d.staffEntryPasswordSet ?? false,
  // 수정 화면은 PopupSlotSection 으로 슬롯을 관리하므로 폼 슬롯 초안은 비운다.
  slots: [],
  // QA-122: locale 별 소개 HTML prefill(en/ja/zh). 정본 ko 는 description 본필드.
  descriptionTranslations: pickLocaleHtml(d.translations, POPUP_DESC_HTML_FIELD),
});

// 개념당 1필드 → base+config 양쪽 컬럼 매핑.
const toRequest = (form: FormState): Omit<PopupRequest, 'popupId' | 'popupKey'> => {
  const region = form.region.trim();
  const address = form.address.trim();
  const orderedDays = DAY_KEYS.filter((d) => form.operatingDays.includes(d));
  const hoursLabel = hoursLabelFrom(form.hoursStart, form.hoursEnd);
  return {
    name: form.name.trim(),
    thumbnailUrl: form.thumbnailUrl.trim(),
    accentHex: form.accentHex,
    isActive: form.isActive,
    sortOrder: form.sortOrder,
    // 지역/주소 → base + config 동일값
    location: region,
    address,
    // 운영 기간 → base open/close + config operatingUntil=closeDate
    openDate: form.openDate,
    closeDate: form.closeDate,
    operatingUntil: form.closeDate || null,
    // 운영 시간 → config 구조화 + base hoursLabel 표시
    hoursLabel,
    operatingHoursStart: toTimeWire(form.hoursStart),
    operatingHoursEnd: toTimeWire(form.hoursEnd),
    // 소개 → config HTML + base plain
    description: stripHtml(form.description),
    descriptionHtml: form.description || null,
    // config 동일값 + 전용
    locationRegion: region || null,
    locationAddress: address || null,
    operatingDays: orderedDays.length > 0 ? orderedDays.join(',') : null,
    locationLat: numberOrNull(form.locationLat),
    locationLng: numberOrNull(form.locationLng),
    snsUrls: form.snsUrls.length > 0 ? JSON.stringify(form.snsUrls) : null,
    basePrice: form.free ? 0 : numberOrNull(form.basePrice),
    // ID36: 빈 입력 = 무제한(null). 잔여석 노출 토글은 항상 명시 전송.
    dailyCapacity: numberOrNull(form.dailyCapacity),
    showRemainingSeats: form.showRemainingSeats,
    // QA-183: 예약 수량 제한. 빈 입력 = 무제한(null) — 임의 디폴트 강제 금지.
    maxPerUser: numberOrNull(form.maxPerUser),
    minPerReservation: numberOrNull(form.minPerReservation),
    maxPerReservation: numberOrNull(form.maxPerReservation),
    // QA-122: locale 별 소개 HTML. 빈 로케일은 제외(미변경/ko 폴백). 모두 비면 생략.
    ...(() => {
      const translations = buildLocaleHtmlTranslations<PopupTranslation>(
        form.descriptionTranslations,
        POPUP_DESC_HTML_FIELD,
      );
      return translations ? { translations } : {};
    })(),
  };
};

export const PopupEditPage = () => {
  const { popupId } = useParams<{ popupId: string }>();
  const navigate = useNavigate();
  // A-1: '/cms/popups/new' 정적 라우트 → 파라미터 없음. 없거나 'new' 이면 등록.
  const isNew = !popupId || popupId === 'new';
  const id = !isNew ? popupId : '';

  const query = usePopupQuery(id);
  const { create, update, remove } = usePopupMutation();
  // L-2: 노출 예약은 별도 config 엔드포인트(PUT /admin/v1/popups/{id}/config). create 시에는
  // 서버에 보낼 popupId 를 먼저 확정해야 config hook 이 그 id 에 바인딩되므로, 신규 id 를 1회 고정한다.
  const newPopupId = useMemo(() => crypto.randomUUID(), []);
  const effectivePopupId = isNew ? newPopupId : id;
  const configMutation = usePopupConfigMutation(effectivePopupId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [snsInput, setSnsInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const uploads = useImageUploadQueue();
  // QA-122 — 팝업 소개 본문 에디터 이미지 업로드(presign→S3→confirm, CMS_POPUP). EventEditPage 와 동일 패턴.
  const uploadImage = usePopupImageUpload();

  useEffect(() => {
    if (query.data) setForm(fromView(query.data));
  }, [query.data]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const setDescriptionTranslation = (locale: ContentLocale, html: string) =>
    setForm((prev) => ({
      ...prev,
      descriptionTranslations: { ...prev.descriptionTranslations, [locale]: html },
    }));

  const toggleDay = (day: DayKey) =>
    set('operatingDays', form.operatingDays.includes(day)
      ? form.operatingDays.filter((d) => d !== day)
      : [...form.operatingDays, day]);

  const addSnsUrl = () => {
    const url = snsInput.trim();
    if (!url || form.snsUrls.includes(url)) return;
    set('snsUrls', [...form.snsUrls, url]);
    setSnsInput('');
  };
  const removeSnsUrl = (url: string) => set('snsUrls', form.snsUrls.filter((u) => u !== url));

  // QA-R6 N: 정원(maxParticipants) 0 기본값 footgun 제거 — slotDraft 는 날짜/시간만 들고,
  // 정원은 slotMaxInput(문자열)에서 파싱한다(빈 입력=무제한). appendSlotDraft 가 maxParticipants 를 주입.
  const [slotDraft, setSlotDraft] = useState<SlotDraftInput>({ slotDate: '', timeStart: '', timeEnd: '' });
  const [slotMaxInput, setSlotMaxInput] = useState('');
  // C-1: 등록 페이지 슬롯 추가 — 판정/누적을 순수 helper 로 분리(회귀 가드, slotDraft.test.ts).
  const canAddSlot = canAddSlotDraft(slotDraft, slotMaxInput);
  const addSlot = () => {
    const next = appendSlotDraft(form.slots, slotDraft, slotMaxInput);
    if (next.length === form.slots.length) return; // 무효 입력 — no-op.
    set('slots', next);
    setSlotDraft({ slotDate: '', timeStart: '', timeEnd: '' });
    setSlotMaxInput('');
  };
  const removeSlot = (index: number) => set('slots', form.slots.filter((_, i) => i !== index));

  const dayItems = useMemo(() => DAY_KEYS.map((d) => ({ key: d, label: DAY_LABEL[d] })), []);

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim()) { setError('팝업명을 입력해 주세요.'); return; }
    // L-2: 노출 예약 on 인데 시작/종료를 둘 다 비우면 게이트 의미가 없으므로 막는다(BE end<start 400 사전회피).
    if (form.displayReservationEnabled) {
      const s = localInputToInstant(form.displayStartLocal);
      const en = localInputToInstant(form.displayEndLocal);
      if (s && en && new Date(en) < new Date(s)) {
        setError('노출 종료 시각은 시작 시각보다 뒤여야 합니다.');
        return;
      }
    }
    // 직원확인 비밀번호 토글 ON 인데 기존 비번도 없고 새 비번도 안 넣으면 게이트 의미가 없으므로 막는다.
    if (
      form.staffEntryPasswordEnabled &&
      !form.staffEntryPassword.trim() &&
      !form.staffEntryPasswordSet
    ) {
      setError('직원확인 비밀번호를 입력해 주세요.');
      return;
    }
    try {
      const resolved = await uploads.flush();
      const thumbnailUrl = typeof resolved.thumbnailUrl === 'string' ? resolved.thumbnailUrl : '';
      const body = { ...toRequest(form), thumbnailUrl };
      if (isNew) {
        await create.mutateAsync({
          ...body,
          popupId: effectivePopupId,
          popupKey: generatePopupKey(form.name),
          // L-2: 등록 시 슬롯 동시 생성. 빈 배열이면 BE no-op.
          ...(form.slots.length > 0 ? { slots: form.slots } : {}),
        });
      } else {
        await update.mutateAsync({ popupId: id, body: { ...body, popupId: id, popupKey: query.data?.popupKey ?? '' } });
      }
      // L-2: 노출 예약은 main 저장(PopupStoreRequest)에 없고 별도 config 엔드포인트로 보낸다.
      //   enabled 를 항상 명시 전송해 토글 off 시 BE 가 윈도우를 비우도록 한다(end<start 는 위에서 가드).
      await configMutation.mutateAsync({
        displayReservationEnabled: form.displayReservationEnabled,
        displayStartAt: form.displayReservationEnabled ? localInputToInstant(form.displayStartLocal) : null,
        displayEndAt: form.displayReservationEnabled ? localInputToInstant(form.displayEndLocal) : null,
        // QA-189: 예매 오픈 일시(노출≠예매). 빈 값=분리 없음(null 전송 → 노출 시점부터 예매).
        bookingOpenAt: localInputToInstant(form.bookingOpenLocal),
        // 직원확인 비밀번호: enabled 는 항상 명시 전송(OFF 시 BE 가 해시 해제). 비번은 ON+입력 있을 때만
        // 전송(빈 값=미변경, 기존 해시 보존). 평문 전송 → BE BCrypt.
        ...buildStaffEntryConfigPayload(form.staffEntryPasswordEnabled, form.staffEntryPassword),
      });
      navigate('/cms/popups');
    } catch (e) {
      setError(friendlyMessage(e, '저장에 실패했습니다.'));
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(id);
      navigate('/cms/popups');
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending || configMutation.isPending;
  // 수정 모드에서 기존 데이터 로드 전(폼이 아직 EMPTY_FORM 상태)에는 저장을 막는다.
  // 막지 않으면 스위치/토글이 기본값(예: isActive=true, showRemainingSeats=false)으로 떠 있는
  // 동안 저장할 경우 하이드레이트되지 않은 stale 값이 그대로 영속된다(하이드레이션 레이스).
  // `isLoading`(v5: enabled+fetching)은 new 모드(enabled=false, idle)에선 false 라 안전하다.
  const isLoadingExisting = !isNew && query.isLoading;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/popups' },
    { label: '팝업 관리', href: '/cms/popups' },
    { label: isNew ? '등록' : '수정' },
  ];

  return (
    <PageShell
      title={isNew ? '팝업 등록' : '팝업 수정'}
      breadcrumb={breadcrumb}
      data-testid="popup-edit-page"
      actions={
        <div className="flex gap-2">
          {/* ID58: 저장된 팝업에 한해 미리보기 URL 생성. */}
          {!isNew && <PreviewUrlButton contentType="POPUP" contentId={id} />}
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              삭제
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || uploads.isUploading || isLoadingExisting}>
            {uploads.isUploading
              ? '이미지 업로드 중…'
              : isLoadingExisting
                ? '불러오는 중…'
                : isNew
                  ? '등록'
                  : '저장'}
          </Button>
        </div>
      }
    >
      {(error || uploads.error) && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error ?? uploads.error}
        </div>
      )}

      <SectionCard title="기본 정보">
        <div className="flex flex-col gap-3">
          <FormSection label="팝업명" density="compact" required>
            <Input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="예) 에버라인 스타팅 라인 팝업스토어" required />
          </FormSection>
          <FormSection label="썸네일" density="compact">
            <ImageUploadField
              queue={uploads}
              name="thumbnailUrl"
              category="CMS_POPUP"
              previewRatio="16:9"
              value={form.thumbnailUrl || null}
              onChange={(url) => set('thumbnailUrl', url ?? '')}
              disabled={isBusy}
            />
          </FormSection>
          <FormSection label="대표 색상" density="compact" hint="배너/카드 강조 색상으로 사용됩니다.">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.accentHex || '#6366f1'}
                onChange={(e) => set('accentHex', e.target.value)}
                aria-label="대표 색상"
                className="h-9 w-12 cursor-pointer rounded border"
                style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
              />
              <Input type="text" value={form.accentHex} onChange={(e) => set('accentHex', e.target.value)} placeholder="#6366f1" className="max-w-[140px] font-mono" />
            </div>
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title="일정 · 위치">
        <div className="flex flex-col gap-3">
          <FormSection label="지역" density="compact">
            <Input type="text" value={form.region} onChange={(e) => set('region', e.target.value)} placeholder="예) 서울" className="max-w-[280px]" />
          </FormSection>
          <FormSection label="상세 주소" density="compact">
            <Input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="예) 서울 성동구 ..." />
          </FormSection>
          <FormSection label="운영 기간" density="compact">
            <div className="flex items-center gap-2">
              <Input type="date" value={form.openDate} onChange={(e) => set('openDate', e.target.value)} className="max-w-[180px]" aria-label="시작일" />
              <span style={{ color: 'var(--content-muted)' }}>~</span>
              <Input type="date" value={form.closeDate} onChange={(e) => set('closeDate', e.target.value)} className="max-w-[180px]" aria-label="종료일" />
            </div>
          </FormSection>
          <FormSection label="운영 시간" density="compact" hint="목록/상세에 'HH:mm - HH:mm' 형태로 노출됩니다.">
            <div className="flex items-center gap-2">
              <Input type="time" value={form.hoursStart} onChange={(e) => set('hoursStart', e.target.value)} className="max-w-[140px]" aria-label="운영 시작 시간" />
              <span style={{ color: 'var(--content-muted)' }}>~</span>
              <Input type="time" value={form.hoursEnd} onChange={(e) => set('hoursEnd', e.target.value)} className="max-w-[140px]" aria-label="운영 종료 시간" />
            </div>
          </FormSection>
          <FormSection label="운영 요일" density="compact">
            <div className="flex flex-wrap gap-3">
              {dayItems.map(({ key, label }) => (
                <label key={key} className="inline-flex items-center gap-1.5 text-[12px]">
                  <Checkbox checked={form.operatingDays.includes(key)} onCheckedChange={() => toggleDay(key)} />
                  <span style={{ color: 'var(--content-default)' }}>{label}</span>
                </label>
              ))}
            </div>
          </FormSection>
          <FormSection label="좌표" density="compact" hint="지도 연동은 후속 예정">
            <div className="flex items-center gap-2">
              <Input inputMode="decimal" placeholder="위도" value={form.locationLat} onChange={(e) => set('locationLat', e.target.value)} className="max-w-[160px]" aria-label="위도" />
              <Input inputMode="decimal" placeholder="경도" value={form.locationLng} onChange={(e) => set('locationLng', e.target.value)} className="max-w-[160px]" aria-label="경도" />
            </div>
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title="콘텐츠 · 혜택">
        <div className="flex flex-col gap-3">
          <FormSection label="SNS URL" density="compact">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Input
                  type="url"
                  value={snsInput}
                  onChange={(e) => setSnsInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSnsUrl(); } }}
                  placeholder="https://instagram.com/..."
                />
                <Button type="button" variant="outline" size="sm" onClick={addSnsUrl}>추가</Button>
              </div>
              {form.snsUrls.length > 0 ? (
                <ul className="flex flex-col gap-1">
                  {form.snsUrls.map((url) => (
                    <li key={url} className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-[12px]" style={{ background: 'var(--surface-1)' }}>
                      <span className="min-w-0 truncate" style={{ color: 'var(--content-default)' }}>{url}</span>
                      <button type="button" aria-label="SNS URL 제거" onClick={() => removeSnsUrl(url)} className="shrink-0 rounded p-0.5" style={{ color: 'var(--signal-danger)' }}>
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </FormSection>
          <FormSection label="참여 비용 (KRW)" density="compact">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-[12px]">
                <Switch checked={form.free} onCheckedChange={(v) => set('free', v)} aria-label="무료" />
                <span style={{ color: 'var(--content-default)' }}>무료</span>
              </label>
              <Input inputMode="numeric" value={form.basePrice} onChange={(e) => set('basePrice', e.target.value)} disabled={form.free} placeholder="0" className="max-w-[160px]" aria-label="참여 비용" />
            </div>
          </FormSection>
          <FormSection label="팝업 소개" density="compact" hint="상세 페이지 소개입니다. 목록/카드에는 서식 없는 요약으로 노출됩니다. 로케일별로 입력할 수 있습니다.">
            {/* QA-122 — 소개 본문을 로케일(ko/en/ja/zh)별로 입력. ko 정본, 타 로케일 빈 값 = SF ko 폴백. */}
            <LocaleHtmlTabs
              baseValue={form.description}
              onBaseChange={(html) => set('description', html)}
              translations={form.descriptionTranslations}
              onTranslationChange={setDescriptionTranslation}
              onUploadImage={uploadImage}
              imageGuide={editorImageGuideFor('POPUP_STORE_BODY')}
              onUploadError={(e) => setError(e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.')}
              ariaLabelPrefix="팝업 소개"
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title="표시 설정">
        <div className="flex flex-col gap-3">
          <FormSection label="활성 여부" density="compact">
            <Switch checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} aria-label="활성 여부" />
          </FormSection>
          {/* ID36: 일별 총 정원. 빈 입력 = 무제한. 예약 시 슬롯 정원과 해당 날짜 전체 예약합을 함께 검사한다. */}
          <FormSection label="일별 총 정원" density="compact" hint="비우면 무제한. 슬롯별 정원과 별개로, 해당 날짜 전체 예약 인원을 제한합니다.">
            <Input
              inputMode="numeric"
              min={0}
              type="number"
              value={form.dailyCapacity}
              onChange={(e) => set('dailyCapacity', e.target.value)}
              placeholder="무제한"
              className="max-w-[160px]"
              aria-label="일별 총 정원"
            />
          </FormSection>
          {/* ID36: 잔여석 공개 노출. 기본 off — 켜면 스토어프론트 슬롯에 잔여석을 표시한다. */}
          <FormSection label="잔여석 노출" density="compact" hint="켜면 스토어프론트 예약 화면에 슬롯별 잔여석을 표시합니다.">
            <Switch
              checked={form.showRemainingSeats}
              onCheckedChange={(v) => set('showRemainingSeats', v)}
              aria-label="잔여석 노출"
            />
          </FormSection>
          {/* QA-183: 예약 수량 제한. 전부 비우면 무제한(임의 디폴트 강제 금지). */}
          <FormSection
            label="ID당 예매 수량 제한"
            density="compact"
            hint="동일 회원(ID)이 누적으로 예매할 수 있는 최대 수량. 비우면 무제한."
          >
            <Input
              inputMode="numeric"
              min={1}
              type="number"
              value={form.maxPerUser}
              onChange={(e) => set('maxPerUser', e.target.value)}
              placeholder="무제한"
              className="max-w-[160px]"
              aria-label="ID당 예매 수량 제한"
            />
          </FormSection>
          <FormSection
            label="1회 예매 수량"
            density="compact"
            hint="1회 예매 시 최소/최대 수량. 비우면 제한 없음."
          >
            <div className="flex items-center gap-2">
              <Input
                inputMode="numeric"
                min={1}
                type="number"
                value={form.minPerReservation}
                onChange={(e) => set('minPerReservation', e.target.value)}
                placeholder="최소"
                className="max-w-[120px]"
                aria-label="1회 예매 최소 수량"
              />
              <span style={{ color: 'var(--content-muted)' }}>~</span>
              <Input
                inputMode="numeric"
                min={1}
                type="number"
                value={form.maxPerReservation}
                onChange={(e) => set('maxPerReservation', e.target.value)}
                placeholder="최대"
                className="max-w-[120px]"
                aria-label="1회 예매 최대 수량"
              />
            </div>
          </FormSection>
          {/* L-2: 노출 예약(예약발행). 켜면 [시작, 종료] 윈도우 안에서만 스토어프론트에 공개된다. */}
          <FormSection label="노출 예약" density="compact" hint="켜면 지정한 노출 시작/종료 시각 사이에만 스토어프론트에 공개됩니다.">
            <Switch
              checked={form.displayReservationEnabled}
              onCheckedChange={(v) => {
                set('displayReservationEnabled', v);
                if (!v) {
                  // 예약 해제 시 윈도우 값 비움(상시 노출로 복귀).
                  set('displayStartLocal', '');
                  set('displayEndLocal', '');
                }
              }}
              aria-label="노출 예약"
            />
          </FormSection>
          {form.displayReservationEnabled ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormSection label="노출 시작" density="compact">
                <Input
                  type="datetime-local"
                  value={form.displayStartLocal}
                  onChange={(e) => set('displayStartLocal', e.target.value)}
                  className="max-w-[220px]"
                  aria-label="노출 시작"
                />
              </FormSection>
              <FormSection label="노출 종료" density="compact" hint="비우면 시작 시각부터 상시 노출(수동 비활성 전까지).">
                <Input
                  type="datetime-local"
                  value={form.displayEndLocal}
                  onChange={(e) => set('displayEndLocal', e.target.value)}
                  className="max-w-[220px]"
                  aria-label="노출 종료"
                />
              </FormSection>
            </div>
          ) : null}
          {/* QA-189: 예매 오픈 일시(노출≠예매오픈). 노출은 먼저 하되 예매는 지정 시각부터 받는다.
              비우면 노출 시점부터 바로 예매(분리 없음). 안내 문구는 SF 가 bookingOpenAt 으로 표시. */}
          <FormSection
            label="예매 오픈 일시"
            density="compact"
            hint="노출과 별개로, 이 시각부터 예매를 받습니다. 비우면 노출 시점부터 바로 예매됩니다. (예: 6/12 노출, 6/13 10:00 예매 오픈)"
          >
            <Input
              type="datetime-local"
              value={form.bookingOpenLocal}
              onChange={(e) => set('bookingOpenLocal', e.target.value)}
              className="max-w-[220px]"
              aria-label="예매 오픈 일시"
            />
          </FormSection>
          {/* 직원확인 비밀번호: 현장 입장 처리(QR 확인) 게이트. ON=비번 입력 화면→이용완료(ENTERED),
              OFF=[직원확인] 단독 버튼. 비밀번호는 팝업 전체 단일(회차/슬롯 공통). 평문 전송, BE 가 BCrypt 해시. */}
          <FormSection
            label="직원확인 비밀번호"
            density="compact"
            hint="켜면 현장에서 QR 확인 후 직원이 비밀번호를 입력해야 입장 처리됩니다. 끄면 비밀번호 없이 [직원 확인] 버튼으로 처리합니다."
          >
            <Switch
              checked={form.staffEntryPasswordEnabled}
              onCheckedChange={(v) => {
                set('staffEntryPasswordEnabled', v);
                // 끄면 입력 중이던 비번을 비운다(전송 안 함). 켜면 기존 비번 표시는 staffEntryPasswordSet 으로.
                if (!v) set('staffEntryPassword', '');
              }}
              aria-label="직원확인 비밀번호 활성화"
            />
          </FormSection>
          {form.staffEntryPasswordEnabled ? (
            <FormSection
              label="비밀번호"
              density="compact"
              required={!form.staffEntryPasswordSet}
              hint={
                form.staffEntryPasswordSet
                  ? '비밀번호가 설정되어 있습니다. 변경하려면 새 비밀번호를 입력하세요. 비우면 기존 비밀번호가 유지됩니다.'
                  : '현장 직원이 입장 처리 시 입력할 비밀번호입니다.'
              }
            >
              <Input
                type="password"
                value={form.staffEntryPassword}
                onChange={(e) => set('staffEntryPassword', e.target.value)}
                placeholder={form.staffEntryPasswordSet ? '변경 시에만 입력 (기존 유지)' : '비밀번호 입력'}
                autoComplete="new-password"
                className="max-w-[280px]"
                aria-label="직원확인 비밀번호"
              />
            </FormSection>
          ) : null}
          <FormSection label="정렬 순서" density="compact">
            <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value))} className="max-w-[160px]" />
          </FormSection>
        </div>
      </SectionCard>

      {isNew ? (
        <SectionCard title="예약 슬롯">
          <p className="mb-3 text-xs" style={{ color: 'var(--content-muted)' }}>
            등록과 함께 생성할 예약 슬롯을 추가할 수 있습니다. 등록 후 운영 화면에서 슬롯을 더 관리할 수 있습니다.
          </p>
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-5">
            {/* QA-56: 슬롯 추가 시 4개 필드 모두 필요(canAddSlot). 슬롯 자체는 팝업 등록에 선택이나
                추가하려면 전부 입력해야 하므로 PopupSlotSection 과 동일하게 필수 표기. */}
            <FormSection label="날짜" density="compact" required>
              <Input type="date" value={slotDraft.slotDate} onChange={(e) => setSlotDraft((p) => ({ ...p, slotDate: e.target.value }))} />
            </FormSection>
            <FormSection label="시작 시간" density="compact" required>
              {/* ID36: 5분 단위(BE minute%5==0 검증). */}
              <TimeSelect5
                value={slotDraft.timeStart}
                onChange={(v) => setSlotDraft((p) => ({ ...p, timeStart: v }))}
                ariaLabel="시작 시간"
              />
            </FormSection>
            <FormSection label="종료 시간" density="compact" required>
              <TimeSelect5
                value={slotDraft.timeEnd}
                onChange={(v) => setSlotDraft((p) => ({ ...p, timeEnd: v }))}
                ariaLabel="종료 시간"
              />
            </FormSection>
            {/* QA-R6 N: 정원 미입력 = 무제한(0 기본값 footgun 제거). BE 가 maxParticipants≤0 을 무제한으로 해석. */}
            <FormSection label="정원" density="compact" hint="비우면 무제한">
              <Input
                inputMode="numeric"
                min={0}
                type="number"
                placeholder="무제한"
                value={slotMaxInput}
                onChange={(e) => setSlotMaxInput(e.target.value)}
              />
            </FormSection>
            <div className="flex items-end">
              <Button size="sm" disabled={!canAddSlot} onClick={addSlot}>슬롯 추가</Button>
            </div>
          </div>
          {form.slots.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {form.slots.map((s, i) => (
                <li
                  key={`${s.slotDate}-${s.timeStart}-${s.timeEnd}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-[13px] tabular-nums"
                  style={{ background: 'var(--surface-1)' }}
                >
                  <span style={{ color: 'var(--content-default)' }}>
                    {/* QA-R6 N: 정원 0(이하) = 무제한 표기. */}
                    {s.slotDate} · {s.timeStart} ~ {s.timeEnd} ·{' '}
                    {s.maxParticipants > 0 ? `정원 ${s.maxParticipants}명` : '정원 무제한'}
                  </span>
                  <button type="button" aria-label="슬롯 제거" onClick={() => removeSlot(i)} className="rounded p-0.5" style={{ color: 'var(--signal-danger)' }}>
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs" style={{ color: 'var(--content-muted)' }}>추가된 슬롯이 없습니다. 슬롯 없이 등록할 수 있습니다.</p>
          )}
        </SectionCard>
      ) : (
        <PopupSlotSection popupId={id} />
      )}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>팝업 삭제</DialogTitle>
            <DialogDescription>"{form.name}" 팝업을 삭제하시겠습니까?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={isBusy} onClick={() => { setConfirmDelete(false); void handleDelete(); }}>
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
